import type { DriverDeliveryRow, DriverProfile, DriverProofPhoto } from '@erp/shared';
import {
  ArrowLeft, CalendarClock, CheckCircle2, Clock, IdCard, Image as ImageIcon, Mail,
  MapPin, Percent, Phone, ShieldCheck, Truck, XCircle, type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge, Card, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/shadcn/index.js';
import { Button, DataTable, EmptyState, StatCard } from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { fetchAttachmentObjectUrl } from '../../lib/attachments.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney, setCurrencyDecimals } from '../../lib/money.js';
import { getDriverProfile, setUserStatus } from '../../lib/users.js';

/** Badge tone per delivery status — mirrors the dispatch board's language. */
function statusTone(s: string): 'success' | 'danger' | 'secondary' {
  if (s === 'delivered') return 'success';
  if (s === 'failed') return 'danger';
  return 'secondary';
}

/**
 * Office profile of ONE driver — the detail view behind each row of Persons →
 * Drivers. A driver is a staff user whose role grants `delivery.deliver`, so
 * this is a read-only roll-up of their identity plus everything the delivery
 * board already knows about them: counters, job history and the
 * proof-of-delivery photos captured for their jobs.
 *
 * Built to look right at ZERO data — most tenants have a driver long before
 * they have deliveries.
 */
export function DriverProfilePage(): JSX.Element {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation('users');
  const { t: tc } = useTranslation();
  const dir = useLayoutDirection();
  const canWrite = usePermission('users.write');

  const [data, setData] = useState<DriverProfile | null>(null);
  const [loading, setLoading] = useState(true);
  /** null = fine. Distinguished so a 403/500 doesn't read as "no such driver". */
  const [failure, setFailure] = useState<'notFound' | 'forbidden' | 'error' | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailure(null);
    void getDriverProfile(id)
      .then((d) => {
        if (!active) return;
        // Merge the payload's decimals so IQD renders as whole dinars even if
        // the global /currencies load hasn't registered that currency yet.
        setCurrencyDecimals(d.currencyDecimals);
        setData(d);
      })
      .catch((e: unknown) => {
        if (!active) return;
        const status = (e as { response?: { status?: number } }).response?.status;
        setFailure(status === 404 ? 'notFound' : status === 403 ? 'forbidden' : 'error');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  const date = (d: string): string => formatDate(d, { locale: i18n.language });
  const money = (a: string, c: string | null): string =>
    c ? formatMoney(a, { locale: i18n.language, currency: c }) : a;

  const backLink = (
    <Link
      to="/persons/drivers"
      className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-muted transition hover:text-fg"
    >
      <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} aria-hidden />
      {t('drivers.title')}
    </Link>
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-5">
        {backLink}
        <Card className="p-6"><Skeleton className="h-8 w-56" /><Skeleton className="mt-4 h-40 w-full" /></Card>
      </div>
    );
  }

  if (failure || !data) {
    return (
      <div className="flex flex-col gap-5">
        {backLink}
        <EmptyState
          title={
            failure === 'forbidden' ? tc('errors:auth.forbidden')
              : failure === 'error' ? tc('errors:server')
                : t('drivers.profile.notFound')
          }
          action={<Link to="/persons/drivers"><Button>{t('drivers.profile.backToList')}</Button></Link>}
        />
      </div>
    );
  }

  const { user, stats, recent, proofs, isDriver, deliveryDataAvailable } = data;
  const initials =
    user.fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';

  const toggleStatus = async (): Promise<void> => {
    const next = user.status === 'disabled' ? 'active' : 'disabled';
    setSaving(true);
    try {
      await setUserStatus(user.id, next);
      setData({ ...data, user: { ...user, status: next } });
      toast.success(t(next === 'active' ? 'drivers.profile.reactivated' : 'drivers.profile.deactivated'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {backLink}

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-border bg-gradient-to-br from-brand/5 via-bg to-bg p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand to-accent text-xl font-bold text-brand-contrast shadow-md ring-1 ring-brand/20">
            {initials}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{user.fullName}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge variant={isDriver ? 'info' : 'secondary'}>
                {isDriver ? t('drivers.profile.role') : t('drivers.profile.notADriver')}
              </Badge>
              <Badge variant={user.status === 'active' ? 'success' : user.status === 'invited' ? 'info' : 'secondary'}>
                {t(`status.${user.status}`)}
              </Badge>
              {user.email && (
                <a href={`mailto:${user.email}`} className="inline-flex items-center gap-1 text-xs transition hover:text-brand" dir="ltr">
                  <Mail className="h-3 w-3" aria-hidden />{user.email}
                </a>
              )}
              {user.phone && (
                <a href={`tel:${user.phone}`} className="inline-flex items-center gap-1 text-xs transition hover:text-brand" dir="ltr">
                  <Phone className="h-3 w-3" aria-hidden />{user.phone}
                </a>
              )}
            </div>
          </div>
        </div>
        {canWrite && (
          <Button
            variant="secondary"
            size="sm"
            loading={saving}
            onClick={() => void toggleStatus()}
          >
            {t(user.status === 'disabled' ? 'drivers.profile.reactivate' : 'drivers.profile.deactivate')}
          </Button>
        )}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label={t('drivers.profile.tiles.total')} value={stats.total} icon={Truck} tone="neutral" />
        <StatCard label={t('drivers.profile.tiles.delivered')} value={stats.delivered} icon={CheckCircle2} tone="success" />
        <StatCard label={t('drivers.profile.tiles.failed')} value={stats.failed} icon={XCircle} tone="danger" />
        <StatCard
          label={t('drivers.profile.tiles.inProgress')}
          value={stats.inProgress}
          hint={stats.inProgress > 0
            ? `${t('drivers.profile.deliveryStatus.assigned')} ${stats.assigned} · ${t('drivers.profile.deliveryStatus.out_for_delivery')} ${stats.outForDelivery}`
            : undefined}
          icon={Clock}
          tone="warning"
        />
        <StatCard
          label={t('drivers.profile.tiles.successRate')}
          value={stats.successRate == null ? '—' : `${stats.successRate}%`}
          hint={stats.successRate == null ? t('drivers.profile.noFinished') : undefined}
          icon={Percent}
          tone="brand"
        />
      </div>

      {/* The module was never migrated into this tenant — say so, rather than
          presenting zeroes that read like a bad score. */}
      {!deliveryDataAvailable && (
        <Card className="border-warning/40 bg-warning/5">
          <p className="text-sm text-fg-muted">{t('drivers.profile.noDeliveryModule')}</p>
        </Card>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="h-10 flex-wrap">
          <TabsTrigger value="overview" className="gap-1.5"><IdCard className="h-4 w-4" aria-hidden />{t('drivers.profile.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="deliveries" className="gap-1.5"><Truck className="h-4 w-4" aria-hidden />{t('drivers.profile.tabs.deliveries')}</TabsTrigger>
          <TabsTrigger value="proofs" className="gap-1.5"><ImageIcon className="h-4 w-4" aria-hidden />{t('drivers.profile.tabs.proofs')}</TabsTrigger>
        </TabsList>

        {/* ── Overview: identity + the roles that make them a driver ── */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <SectionTitle icon={IdCard}>{t('drivers.profile.identity')}</SectionTitle>
              <dl className="flex flex-col gap-0.5 text-sm">
                <InfoRow icon={Mail} label={t('invite.email')} value={user.email} href={`mailto:${user.email}`} />
                <InfoRow icon={Phone} label={t('invite.phone')} value={user.phone} href={user.phone ? `tel:${user.phone}` : undefined} />
                <InfoRow icon={ShieldCheck} label={t('list.columns.status')} value={t(`status.${user.status}`)} />
                <InfoRow icon={CalendarClock} label={t('drivers.profile.since')} value={date(user.createdAt)} />
                <InfoRow
                  icon={Clock}
                  label={t('list.columns.lastLoginAt')}
                  value={user.lastLoginAt ? date(user.lastLoginAt) : t('drivers.profile.neverSignedIn')}
                />
              </dl>
            </Card>

            <Card>
              <SectionTitle icon={ShieldCheck}>{t('list.columns.roles')}</SectionTitle>
              {user.roles.length === 0 ? (
                <p className="py-2 text-sm text-fg-muted">{t('drivers.profile.noRoles')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {user.roles.map((r) => (
                    <Badge key={r} variant={user.driverRoles.includes(r) ? 'info' : 'secondary'}>{r}</Badge>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-fg-muted">
                {user.driverRoles.length > 0
                  ? t('drivers.profile.driverVia', { roles: user.driverRoles.join(', ') })
                  : t('drivers.profile.notADriverHint')}
              </p>
            </Card>
          </div>
        </TabsContent>

        {/* ── Deliveries ── */}
        <TabsContent value="deliveries">
          {recent.length === 0 ? (
            <Card className="p-6">
              <EmptyState
                icon={<Truck className="h-8 w-8" aria-hidden />}
                title={t('drivers.profile.empty.deliveries.title')}
                description={t('drivers.profile.empty.deliveries.description')}
              />
            </Card>
          ) : (
            <Card className="p-0">
              <DataTable
                columns={deliveryColumns(t, money, date)}
                data={recent}
                rowKey={(r: DriverDeliveryRow) => r.id}
                initialSort={{ id: 'date', dir: 'desc' }}
              />
            </Card>
          )}
        </TabsContent>

        {/* ── Proof-of-delivery photos ── */}
        <TabsContent value="proofs">
          {proofs.length === 0 ? (
            <Card className="p-6">
              <EmptyState
                icon={<ImageIcon className="h-8 w-8" aria-hidden />}
                title={t('drivers.profile.empty.proofs.title')}
                description={t('drivers.profile.empty.proofs.description')}
              />
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {proofs.map((p) => <ProofTile key={p.attachmentId} proof={p} date={date} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

type TFn = ReturnType<typeof useTranslation>['t'];

function deliveryColumns(
  t: TFn,
  money: (a: string, c: string | null) => string,
  date: (d: string) => string,
) {
  return [
    {
      id: 'number',
      header: t('drivers.profile.cols.number'),
      cell: (r: DriverDeliveryRow) => (
        <Link to={`/delivery/${r.id}`} className="font-mono font-medium text-brand hover:underline">
          {r.docNumber}
        </Link>
      ),
      searchValue: (r: DriverDeliveryRow) => `${r.docNumber} ${r.partyName} ${r.recipientName ?? ''} ${r.city ?? ''}`,
      sortValue: (r: DriverDeliveryRow) => r.docNumber,
    },
    {
      id: 'destination',
      header: t('drivers.profile.cols.destination'),
      cell: (r: DriverDeliveryRow) => (
        <div className="min-w-0">
          <Link
            to={`/sales/parties/${r.partyId}`}
            className="block truncate font-medium hover:text-brand hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.partyName || r.recipientName || '—'}
          </Link>
          {r.city && (
            <span className="inline-flex items-center gap-1 truncate text-xs text-fg-muted">
              <MapPin className="h-3 w-3" aria-hidden />{r.city}
            </span>
          )}
        </div>
      ),
      sortValue: (r: DriverDeliveryRow) => r.partyName,
    },
    {
      id: 'status',
      header: t('drivers.profile.cols.status'),
      cell: (r: DriverDeliveryRow) => (
        <Badge variant={statusTone(r.status)}>
          {t(`drivers.profile.deliveryStatus.${r.status}`, { defaultValue: r.status })}
        </Badge>
      ),
      sortValue: (r: DriverDeliveryRow) => r.status,
    },
    {
      id: 'assigned',
      header: t('drivers.profile.cols.assigned'),
      cell: (r: DriverDeliveryRow) => (
        <span className="whitespace-nowrap text-fg-muted">{r.assignedAt ? date(r.assignedAt) : '—'}</span>
      ),
      sortValue: (r: DriverDeliveryRow) => r.assignedAt ?? '',
    },
    {
      id: 'date',
      header: t('drivers.profile.cols.delivered'),
      cell: (r: DriverDeliveryRow) => (
        <span className="whitespace-nowrap text-fg-muted">{r.deliveredAt ? date(r.deliveredAt) : '—'}</span>
      ),
      sortValue: (r: DriverDeliveryRow) => r.deliveredAt ?? r.createdAt,
    },
    {
      id: 'total',
      header: t('drivers.profile.cols.total'),
      align: 'end' as const,
      cell: (r: DriverDeliveryRow) => (
        <span className="font-mono tabular-nums">{r.orderTotal != null ? money(r.orderTotal, r.currencyCode) : '—'}</span>
      ),
      sortValue: (r: DriverDeliveryRow) => (r.orderTotal != null ? Number(r.orderTotal) : null),
    },
  ];
}

/**
 * One proof photo. Attachment downloads are bearer-token gated (and served
 * `Content-Disposition: attachment`), so the bytes are fetched through the
 * authed client and wrapped in an object URL — a bare <img src> can't work.
 */
function ProofTile({ proof, date }: { proof: DriverProofPhoto; date: (d: string) => string }): JSX.Element {
  const { t } = useTranslation('users');
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let created: string | null = null;
    void fetchAttachmentObjectUrl(proof.attachmentId)
      .then((u) => {
        created = u;
        if (active) setUrl(u);
        else URL.revokeObjectURL(u); // unmounted mid-flight — don't leak the blob
      })
      .catch(() => { if (active) setFailed(true); });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [proof.attachmentId]);

  return (
    <Card className="flex flex-col gap-2 overflow-hidden p-3">
      <div className="grid aspect-square place-items-center overflow-hidden rounded-lg bg-bg-subtle">
        {failed ? (
          <ImageIcon className="h-8 w-8 text-fg-subtle" aria-hidden />
        ) : url ? (
          <img src={url} alt={proof.filename} className="h-full w-full object-cover" />
        ) : (
          <Skeleton className="h-full w-full" />
        )}
      </div>
      <div className="min-w-0">
        <Link
          to={`/delivery/${proof.deliveryId}`}
          className="block truncate font-mono text-xs font-medium text-brand hover:underline"
        >
          {proof.deliveryNumber}
        </Link>
        {proof.recipientName && <div className="truncate text-xs text-fg-muted">{proof.recipientName}</div>}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <span className="text-[0.68rem] text-fg-subtle">{date(proof.uploadedAt)}</span>
          <Badge variant={statusTone(proof.deliveryStatus)}>
            {t(`drivers.profile.deliveryStatus.${proof.deliveryStatus}`, { defaultValue: proof.deliveryStatus })}
          </Badge>
        </div>
      </div>
    </Card>
  );
}

function SectionTitle({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-4 flex items-center gap-2 border-b border-border/60 pb-2.5 text-[0.7rem] font-bold uppercase tracking-wider text-fg-subtle">
      {Icon && <Icon className="h-3.5 w-3.5 text-brand" aria-hidden />}
      {children}
    </h2>
  );
}

function InfoRow({ icon: Icon, label, value, href }: {
  icon: LucideIcon;
  label: string;
  value?: string | null;
  href?: string;
}): JSX.Element {
  const isLink = Boolean(value && href);
  return (
    <div className="group flex items-center gap-3 rounded-lg px-1 py-1.5 transition-colors hover:bg-bg-subtle/60">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-bg-subtle text-fg-subtle transition-colors group-hover:bg-brand/10 group-hover:text-brand">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-[0.68rem] font-medium uppercase tracking-wide text-fg-subtle">{label}</dt>
        <dd className="truncate text-sm font-semibold text-fg">
          {value ? (
            isLink ? (
              <a href={href} className="underline-offset-4 transition-colors hover:text-brand hover:underline" dir="ltr">{value}</a>
            ) : value
          ) : (
            <span className="font-normal text-fg-muted">—</span>
          )}
        </dd>
      </div>
    </div>
  );
}
